import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AvatarView } from '../../components/ui/AvatarView';
import { PublicSiteHeader } from '../../components/PublicSiteHeader';
import { PublicSiteFooter } from '../../components/PublicSiteFooter';
import { fetchPublicCatalog, type PublicCatalogCard } from '../../api/dataApi';
import { DEFAULT_ACCENT_HEX } from '../../lib/brand';
import { useInfiniteScroll } from '../../lib/useInfiniteScroll';
import { Sparkline } from '../../components/charts/Sparkline';
import { track } from '../../lib/analytics';
import { useFeatureStrict } from '../../lib/features';
import { freshnessOf, FRESHNESS_LABEL, type Freshness } from '../../lib/freshness';

const FRESHNESS_STYLE: Record<Freshness, string> = {
  fresh: 'bg-(--color-accent)/15 text-(--color-accent-2) dark:text-(--color-accent)',
  stale: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  no_data: 'bg-black/[0.06] dark:bg-white/[0.08] text-black/55 dark:text-white/55',
};

// First page is the "top 10"; later pages are appended as the visitor
// scrolls. The server returns best-ranked first (channel:rank:public), so
// list order is the server's featured order (channel:rank:public); it is
// not presented as a ranking to visitors.
const PAGE_SIZE = 10;

function timeAgo(ms: number): string {
  if (!ms) return 'No activity yet';
  const mins = Math.floor((Date.now() - ms) / 60000);
  if (mins < 1) return 'Updated just now';
  if (mins < 60) return `Updated ${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Updated ${hours}h ago`;
  return `Updated ${Math.floor(hours / 24)}d ago`;
}

// zmist.co/public's twin inside the app: the catalog of public
// channels as big cards (two per row at most), loaded page by page with
// infinite scroll. Logged-out friendly - same chrome as the other
// /public/* screens and no auth anywhere on the path.
export function PublicChannelsListScreen() {
  const searchOn = useFeatureStrict('explore.v2');
  const [cards, setCards] = useState<PublicCatalogCard[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const offsetRef = useRef(0);
  const [query, setQuery] = useState('');
  const queryRef = useRef('');
  const reqRef = useRef(0);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Public channels — Zmist';
    return () => {
      document.title = prev;
    };
  }, []);

  const loadMore = async () => {
    const reqId = reqRef.current;
    try {
      const page = await fetchPublicCatalog(queryRef.current, offsetRef.current, PAGE_SIZE);
      // A newer search superseded this request - drop the stale response.
      if (reqId !== reqRef.current) return;
      offsetRef.current += PAGE_SIZE;
      // Cards for channels that went private since indexing are dropped
      // server-side, so a page can come back short - trust hasMore, not
      // the page length. De-dupe by nickname in case ranks shift between
      // page fetches.
      setCards((prev) => {
        const seen = new Set(prev.map((c) => c.nickname));
        return [...prev, ...page.cards.filter((c) => !seen.has(c.nickname))];
      });
      setHasMore(offsetRef.current < page.total);
      setError(null);
    } catch (e) {
      if (reqId !== reqRef.current) return;
      setError(e instanceof Error ? e.message : 'Unable to load public channels.');
      setHasMore(false);
    } finally {
      if (reqId === reqRef.current) setLoading(false);
    }
  };

  // Debounced search: reset the paging state, then load page one.
  useEffect(() => {
    const q = query.trim();
    if (q === queryRef.current && reqRef.current > 0) return;
    const t = window.setTimeout(() => {
      queryRef.current = q;
      reqRef.current += 1;
      offsetRef.current = 0;
      setCards([]);
      setHasMore(true);
      setError(null);
      setLoading(true);
      void loadMore();
    }, reqRef.current === 0 ? 0 : 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    track('explore_viewed');
  }, []);

  const sentinelRef = useInfiniteScroll(loadMore, hasMore && !loading);

  return (
    <div className="min-h-screen flex flex-col bg-(--color-bg) text-black dark:text-white">
      <PublicSiteHeader />
      <main className="flex-1 w-full max-w-[1100px] mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="mb-8">
          <span className="text-[12px] font-semibold uppercase tracking-wider text-(--color-accent)">
            Public channels
          </span>
          <h1 className="text-[28px] sm:text-[36px] font-extrabold tracking-tight mt-1 mb-2">
            Live public channels
          </h1>
          <p className="text-[15px] text-black/60 dark:text-white/60 max-w-[620px] m-0">
            Every public channel is real data with a chart and an AI report. Open one to see what Zmist does before
            you connect anything of your own.
          </p>
        </div>

        {searchOn && (
        <form role="search" onSubmit={(e) => e.preventDefault()} className="mb-6">
          <label htmlFor="public-search" className="sr-only">
            Search public channels
          </label>
          <input
            id="public-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search public channels…"
            className="w-full max-w-[520px] rounded-2xl px-4 py-3 text-[15px] bg-white dark:bg-white/5 border border-black/10 dark:border-white/10"
          />
        </form>
        )}

        {loading && cards.length === 0 && (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin text-black/40 dark:text-white/40" size={28} />
          </div>
        )}

        {error && cards.length === 0 && (
          <p className="text-center py-16 text-black/60 dark:text-white/60">{error}</p>
        )}

        {!loading && !error && cards.length === 0 && (
          <p className="text-center py-16 text-black/60 dark:text-white/60">
            {queryRef.current ? `No public channels match “${queryRef.current}”.` : 'No public channels yet.'}
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {cards.map((c) => (
            <Link
              key={c.nickname}
              to={`/public/${encodeURIComponent(c.nickname)}`}
              className="group relative flex flex-col gap-4 rounded-3xl p-6 sm:p-7 min-h-[220px] bg-white dark:bg-white/[0.04] border border-black/[0.06] dark:border-white/[0.08] hover:border-(--color-accent)/50 hover:shadow-lg transition-all no-underline text-inherit"
            >
              <div className="flex items-start justify-between gap-3">
                <AvatarView symbol={c.avatarSymbol || 'hash'} colorHex={c.accentHex || DEFAULT_ACCENT_HEX} imageKey={c.avatarImageKey} size={56} />
                <span
                  className={`rounded-full px-2.5 py-1 text-[12px] font-semibold ${FRESHNESS_STYLE[freshnessOf(c.lastUpdatedAt)]}`}
                >
                  {FRESHNESS_LABEL[freshnessOf(c.lastUpdatedAt)]}
                </span>
              </div>
              <div className="min-w-0">
                <h2 className="text-[20px] font-bold tracking-tight m-0 mb-1 truncate">{c.title || c.nickname}</h2>
                {c.description && (
                  <p className="text-[14px] leading-relaxed text-black/65 dark:text-white/65 m-0 line-clamp-3">
                    {c.description}
                  </p>
                )}
              </div>
              <Sparkline
                points={c.spark}
                color={c.accentHex || DEFAULT_ACCENT_HEX}
                label={`${c.title || c.nickname} recent activity`}
              />
              <div className="mt-auto flex flex-wrap items-center gap-2 text-[12px]">
                {(c.tags ?? []).slice(0, 3).map((t) => (
                  <span
                    key={t}
                    className="rounded-full px-2.5 py-1 bg-black/[0.05] dark:bg-white/[0.08] text-black/60 dark:text-white/60"
                  >
                    {t}
                  </span>
                ))}
                <span className="ml-auto text-black/45 dark:text-white/45">{timeAgo(c.lastUpdatedAt)}</span>
              </div>
            </Link>
          ))}
        </div>

        {hasMore && cards.length > 0 && (
          <div ref={sentinelRef} className="flex justify-center py-10">
            <Loader2 className="animate-spin text-black/40 dark:text-white/40" size={22} />
          </div>
        )}
      </main>
      <PublicSiteFooter />
    </div>
  );
}
